import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, PageBody } from "@/components/PageChrome";
import { Input, Select, Button } from "@/components/controls";
import { RefreshCcw, ExternalLink, Repeat, History } from "lucide-react";
import { T } from "@/lib/testIds";
import { usePerms } from "@/lib/auth";

const KIND_OPTS = ["inventory", "procurement"];

export default function AllocationHistory() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ kind: "", system_sku: "", q: "", start: "", end: "" });
  const { canEdit } = usePerms();
  const editable = canEdit("orders");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [{ data }, { data: sm }] = await Promise.all([
        api.get("/allocation-history", { params }),
        api.get("/allocation-history/summary"),
      ]);
      setRows(data);
      setSummary(sm);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const applyFilters = () => load();

  const clearFilters = () => {
    setFilters({ kind: "", system_sku: "", q: "", start: "", end: "" });
    setTimeout(load, 0);
  };

  const removeEvent = async (row) => {
    if (!row.procurement_id || !row.order_id) return;
    if (!window.confirm(`Release ${row.qty} unit(s) from order #${row.system_id} back to procurement China #${row.procurement_china_id}?`)) return;
    try {
      await api.post(`/orders/${row.order_id}/release-procurement`, { procurement_id: row.procurement_id });
      toast.success(`Released ${row.qty} units`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reallocateOrder = async (row) => {
    if (!row.order_id) return;
    if (!window.confirm(`Re-run allocation pipeline for order #${row.system_id}?`)) return;
    try {
      const { data } = await api.post(`/orders/${row.order_id}/reallocate`);
      toast.success(`Reallocated. Inv:${data.inventory_allocated_qty} Proc:${data.procurement_allocated_qty}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openOrder = (row) => {
    if (row.order_id) window.open(`/orders/${row.order_id}`, "_blank");
  };

  return (
    <>
      <PageHeader title="Allocation History" subtitle="Every inventory + procurement allocation across all orders" testId="allocation-history-page">
        <Button onClick={load} disabled={loading} testId="alloc-history-refresh-btn">
          <RefreshCcw size={13} /> {loading ? "Loading…" : "Refresh"}
        </Button>
      </PageHeader>
      <PageBody>
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="alloc-summary-tiles">
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Orders w/ Inventory Alloc</div>
              <div className="font-mono text-2xl font-bold text-emerald-800">{summary.inventory_allocated_orders}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Orders w/ Procurement Alloc</div>
              <div className="font-mono text-2xl font-bold text-indigo-800">{summary.procurement_allocated_orders}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Procurement Events</div>
              <div className="font-mono text-2xl font-bold text-slate-800">{summary.procurement_events}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Procurement Qty Total</div>
              <div className="font-mono text-2xl font-bold text-slate-800">{summary.procurement_qty_total}</div>
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-sm p-3 mb-3 flex flex-wrap gap-2 items-end" data-testid="alloc-filter-bar">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Kind</div>
            <Select value={filters.kind} onChange={(v) => setFilters((f) => ({ ...f, kind: v }))} options={KIND_OPTS} testId="alloc-filter-kind" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">System SKU</div>
            <Input value={filters.system_sku} onChange={(e) => setFilters((f) => ({ ...f, system_sku: e.target.value }))} placeholder="Exact SKU" testId="alloc-filter-sku" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Search</div>
            <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Product / MI / user…" testId="alloc-filter-q" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Start Date</div>
            <input type="date" value={filters.start} onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))} className="h-8 px-2 text-[12px] border border-slate-200 rounded-sm" data-testid="alloc-filter-start" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">End Date</div>
            <input type="date" value={filters.end} onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))} className="h-8 px-2 text-[12px] border border-slate-200 rounded-sm" data-testid="alloc-filter-end" />
          </div>
          <div className="flex gap-2">
            <Button onClick={applyFilters} variant="primary" testId="alloc-filter-apply">Apply</Button>
            <Button onClick={clearFilters} testId="alloc-filter-clear">Clear</Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-[11px] font-mono border-collapse" data-testid="alloc-history-table">
            <thead>
              <tr className="text-left text-slate-500 uppercase tracking-wider border-b border-slate-200 bg-slate-50">
                <th className="py-1.5 px-2">Kind</th>
                <th className="py-1.5 px-2">System ID</th>
                <th className="py-1.5 px-2">SKU</th>
                <th className="py-1.5 px-2">Qty</th>
                <th className="py-1.5 px-2">Source</th>
                <th className="py-1.5 px-2">Marketplace</th>
                <th className="py-1.5 px-2">Account</th>
                <th className="py-1.5 px-2">Working Status</th>
                <th className="py-1.5 px-2">Allocated At</th>
                <th className="py-1.5 px-2">By</th>
                <th className="py-1.5 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`alloc-history-row-${i}`}>
                  <td className="py-1.5 px-2">
                    <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-sm ${r.kind === "inventory" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-indigo-50 text-indigo-800 border border-indigo-200"}`}>{r.kind}</span>
                  </td>
                  <td className="py-1.5 px-2">
                    <button onClick={() => openOrder(r)} className="text-blue-700 hover:underline inline-flex items-center gap-1" data-testid={`alloc-open-order-${i}`}>
                      #{r.system_id ?? "—"} <ExternalLink size={10} />
                    </button>
                  </td>
                  <td className="py-1.5 px-2">{r.system_sku || "—"}</td>
                  <td className="py-1.5 px-2 font-bold">{r.qty}</td>
                  <td className="py-1.5 px-2">
                    {r.kind === "inventory"
                      ? (r.warehouse_name || "—")
                      : (r.procurement_china_id ? `China #${r.procurement_china_id}` : "—")}
                  </td>
                  <td className="py-1.5 px-2 text-slate-600">{r.marketplace || "—"}</td>
                  <td className="py-1.5 px-2 text-slate-600">{r.account_id || "—"}</td>
                  <td className="py-1.5 px-2 text-slate-600">{r.working_status || "—"}</td>
                  <td className="py-1.5 px-2 text-slate-500">{r.allocated_at ? new Date(r.allocated_at).toLocaleString() : "—"}</td>
                  <td className="py-1.5 px-2 text-slate-500">{r.allocated_by || "—"}</td>
                  <td className="py-1.5 px-2">
                    {r.kind === "procurement" && editable && (
                      <button onClick={() => removeEvent(r)} data-testid={`alloc-remove-${i}`} className="h-6 px-2 text-[10px] font-semibold rounded-sm border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 mr-1">Remove</button>
                    )}
                    {editable && (
                      <button onClick={() => reallocateOrder(r)} data-testid={`alloc-reallocate-${i}`} className="h-6 px-2 text-[10px] font-semibold rounded-sm border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"><Repeat size={10} /> Reallocate</button>
                    )}
                  </td>
                </tr>
              ))}
              {!rows.length && !loading && (
                <tr><td colSpan={11} className="py-8 text-center text-slate-400">No allocation events found for the current filters.</td></tr>
              )}
              {loading && (
                <tr><td colSpan={11} className="py-8 text-center text-slate-400">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="text-[10.5px] text-slate-400 mt-2 font-mono">{rows.length} event(s) shown</div>
      </PageBody>
    </>
  );
}
